import React, { createContext, useContext, useState, useCallback } from 'react';
import catalogService from '../utils/catalogService';
import formsService from '../utils/formsService';
import { debugLog } from '../utils/debugLog';

// Crear el contexto para compartir datos entre pasos
const DatosApiContext = createContext();

// Hook personalizado para usar el contexto
export const useDatosApi = () => {
  const contexto = useContext(DatosApiContext);
  if (!contexto) {
    throw new Error('useDatosApi debe ser usado dentro de un ProveedorDatosApi');
  }
  return contexto;
};

// Estados posibles de carga
const ESTADOS_CARGA = {
  INICIAL: 'inicial',
  CARGANDO: 'cargando',
  COMPLETADO: 'completado',
  ERROR: 'error'
};

// Proveedor del contexto
export const ProveedorDatosApi = ({ children }) => {
  // Estados de los datos
  const [estadoCarga, setEstadoCarga] = useState(ESTADOS_CARGA.INICIAL);
  const [progresoCarga, setProgresoCarga] = useState(0);
  const [errores, setErrores] = useState({});
  
  // Datos del formulario
  const [datosFormulario, setDatosFormulario] = useState({
    preguntas: [],
    infoFicha: null,
    infoGeneral: null
  });
  
  // Datos de catálogos
  const [datosCatalogos, setDatosCatalogos] = useState(new Map());
  
  // UUID de la ficha actual
  const [fichaUuid, setFichaUuid] = useState(null);

  /**
   * Limpia todos los datos y errores
   */
  const limpiarDatos = useCallback(() => {
    // // debugLog('🧹 [API] Limpiando todos los datos...');
    setDatosFormulario({
      preguntas: [],
      infoFicha: null,
      infoGeneral: null
    });
    setDatosCatalogos(new Map());
    setErrores({});
    setProgresoCarga(0);
    setEstadoCarga(ESTADOS_CARGA.INICIAL);
    setFichaUuid(null);
  }, []);

  /**
   * Carga los datos del formulario desde la API
   * @param {string} uuid - UUID de la ficha
   */
  const cargarDatosFormulario = useCallback(async (uuid) => {
    if (!uuid) {
      throw new Error('UUID de ficha es requerido');
    }

    debugLog('📋 [API] Iniciando carga de datos del formulario...', { uuid });
    
    try {
      setProgresoCarga(25);
      
      // Cargar datos del formulario usando formsService
      const datosForm = await formsService.loadFormByUuid(uuid);
      
      setDatosFormulario({
        preguntas: datosForm.questions || [],
        infoFicha: datosForm.infoFicha || null,
        infoGeneral: datosForm.infoGeneral || null
      });
      
      setProgresoCarga(50);
      
      debugLog('✅ [API] Datos del formulario cargados exitosamente:', {
        totalPreguntas: datosForm.questions?.length || 0,
        tieneInfoFicha: !!datosForm.infoFicha,
        tieneInfoGeneral: !!datosForm.infoGeneral
      });
      
      return datosForm;
    } catch (error) {
      console.error('❌ [API] Error al cargar datos del formulario:', error);
      setErrores(prev => ({
        ...prev,
        formulario: error.message
      }));
      throw error;
    }
  }, []);

  /**
   * Carga los catálogos desde la API
   * @param {string} [uuid] - uuid de la ficha, para filtrar catálogos por su id_cultivo
   */
  const cargarCatalogos = useCallback(async (uuid) => {
    debugLog('📚 [API] Iniciando carga de catálogos...');

    try {
      setProgresoCarga(prev => prev + 25);

      // Cargar catálogos usando catalogService, filtrados por el cultivo de esta ficha
      const catalogos = await catalogService.loadCatalogs(uuid);
      
      setDatosCatalogos(catalogos);
      
      setProgresoCarga(prev => prev + 25);
      
      // Obtener estadísticas de los catálogos
      const estadisticas = await catalogService.getStatistics();
      
      // // debugLog('✅ [API] Catálogos cargados exitosamente:', {
      //   totalListas: estadisticas.totalListas,
      //   totalOpciones: estadisticas.totalOpciones,
      //   promedioOpcionesPorLista: estadisticas.averageOptionsPerList
      // });
      
      return catalogos;
    } catch (error) {
      // // console.error('❌ [API] Error al cargar catálogos:', error);
      setErrores(prev => ({
        ...prev,
        catalogos: error.message
      }));
      throw error;
    }
  }, []);

  /**
   * Carga todos los datos necesarios de la API
   * @param {string} uuid - UUID de la ficha
   */
  const cargarTodosLosDatos = useCallback(async (uuid) => {
    if (!uuid) {
      throw new Error('UUID de ficha es requerido');
    }

    debugLog('🚀 [API] Iniciando carga completa de datos...', { uuid });
    
    try {
      setEstadoCarga(ESTADOS_CARGA.CARGANDO);
      setFichaUuid(uuid);
      setErrores({});
      setProgresoCarga(0);
      
      // Cargar datos en paralelo para mejor rendimiento
      const [datosForm, catalogos] = await Promise.all([
        cargarDatosFormulario(uuid),
        cargarCatalogos(uuid)
      ]);
      
      setProgresoCarga(100);

      // Validación: si configForm (questions) o infoGeneral vienen vacíos, marcar error y no generar formulario
      const configFormVacio = !Array.isArray(datosForm?.questions) || datosForm.questions.length === 0;
      const infoGeneralVacia = !datosForm?.infoGeneral || (typeof datosForm.infoGeneral === 'object' && Object.keys(datosForm.infoGeneral).length === 0);

      if (configFormVacio || infoGeneralVacia) {
        const nuevosErrores = {};
        if (configFormVacio) {
          nuevosErrores.configForm = 'La configuración del formulario (configForm) está vacía. Es posible que id_kobo_ficha_principal esté como NULL.';
        }
        if (infoGeneralVacia) {
          nuevosErrores.infoGeneral = 'La información general (infoGeneral) está vacía. Verifica el UUID o la API.';
        }

        setErrores(prev => ({
          ...prev,
          ...nuevosErrores
        }));
        setEstadoCarga(ESTADOS_CARGA.ERROR);

        console.error('⚠️ [API] Validación fallida: datos vacíos', { uuid, configFormVacio, infoGeneralVacia });

        // Retornar igualmente para consistencia, pero la UI mostrará errores y no intentará generar
        return {
          formulario: datosForm,
          catalogos: catalogos
        };
      }

      setEstadoCarga(ESTADOS_CARGA.COMPLETADO);
      
      debugLog('🎉 [API] Carga completa de datos finalizada exitosamente:', {
        uuid,
        preguntas: datosForm.questions?.length || 0,
        catalogos: catalogos.size || 0,
        tiempoTotal: 'completado'
      });
      
      return {
        formulario: datosForm,
        catalogos: catalogos
      };
      
    } catch (error) {
      console.error('💥 [API] Error en la carga completa de datos:', error);
      setEstadoCarga(ESTADOS_CARGA.ERROR);
      throw error;
    }
  }, []);

  /**
   * Refresca todos los datos desde la API
   */
  const refrescarDatos = useCallback(async () => {
    if (!fichaUuid) {
      throw new Error('No hay UUID de ficha para refrescar');
    }
    
    // // debugLog('🔄 [API] Refrescando datos...', { fichaUuid });
    
    // Limpiar datos actuales
    limpiarDatos();
    
    // Cargar datos frescos
    return await cargarTodosLosDatos(fichaUuid);
  }, [fichaUuid, limpiarDatos, cargarTodosLosDatos]);

  /**
   * Obtiene las opciones de un catálogo específico
   * @param {string} nombreLista - Nombre de la lista del catálogo
   * @returns {Array} Opciones del catálogo
   */
  const obtenerOpcionesCatalogo = useCallback((nombreLista) => {
    if (!nombreLista) {
      // // console.warn('⚠️ [API] Nombre de lista no proporcionado para obtener opciones');
      return [];
    }

    const opciones = datosCatalogos.get(nombreLista) || [];
    if (opciones.length === 0) {
      // // console.warn('⚠️ [API] No se encontraron opciones para la lista:', nombreLista);
    }

    return opciones;
  }, [datosCatalogos]);

  /**
   * Verifica si los datos están completamente cargados
   * @returns {boolean}
   */
  const datosEstanCargados = useCallback(() => {
    return estadoCarga === ESTADOS_CARGA.COMPLETADO &&
           datosFormulario.preguntas.length > 0 &&
           datosCatalogos.size > 0;
  }, [estadoCarga, datosFormulario.preguntas.length, datosCatalogos.size]);

  /**
   * Obtiene información de estado para debugging
   * @returns {Object}
   */
  const obtenerEstadoDebug = useCallback(() => {
    return {
      estadoCarga,
      progresoCarga,
      fichaUuid,
      totalPreguntas: datosFormulario.preguntas.length,
      totalCatalogos: datosCatalogos.size,
      tieneErrores: Object.keys(errores).length > 0,
      errores,
      datosCompletos: datosEstanCargados()
    };
  }, [estadoCarga, progresoCarga, fichaUuid, datosFormulario.preguntas.length, 
      datosCatalogos.size, errores, datosEstanCargados]);

  // Valor del contexto
  const valorContexto = {
    // Estados
    estadoCarga,
    progresoCarga,
    errores,
    fichaUuid,
    
    // Datos
    datosFormulario,
    datosCatalogos,
    
    // Métodos de carga
    cargarTodosLosDatos,
    refrescarDatos,
    limpiarDatos,
    
    // Métodos de consulta
    obtenerOpcionesCatalogo,
    datosEstanCargados,
    obtenerEstadoDebug,
    
    // Constantes
    ESTADOS_CARGA
  };

  return (
    <DatosApiContext.Provider value={valorContexto}>
      {children}
    </DatosApiContext.Provider>
  );
};

export default ProveedorDatosApi;